import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useContent } from "../content/ContentContext";
import { setPageMeta } from "../lib/seo";
import { SectionLabel, ButtonLink, Tag } from "../components/Ui";
import { Reveal } from "../components/Reveal";
import { CaseVisual } from "../dashboards/CaseVisual";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const { content } = useContent();
  const { caseStudies, dashboards, process, services, packages } = content;
  const cs = caseStudies.find((c) => c.id === id);
  useEffect(() => {
    if (!cs) return;
    setPageMeta(`${cs.title} — Case Study — Savvy Stephanie Analytics & Consulting`, cs.summary, {
      path: `/case-studies/${cs.id}`,
      share: {
        title: cs.shareTitle || `${cs.title} — Case Study`,
        description: cs.shareDescription || cs.summary,
        image: cs.shareImage || "",
      },
    });
  }, [cs]);
  if (!cs) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h1 className="font-serif text-3xl text-ink">Case study not found</h1>
        <ButtonLink to="/case-studies" variant="secondary" testid="case-study-back-link" className="mt-8">
          Back to Case Studies
        </ButtonLink>
      </main>
    );
  }
  const relatedService = services.find((s) => s.id === cs.relatedService);
  const relatedPackage = packages.find((p) => p.id === cs.relatedPackage);
  return (
    <main className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-testid="case-study-page">
      <Reveal>
        <Link to="/case-studies" className="text-sm font-semibold text-plum hover:text-accent" data-testid="case-study-back">
          ← All case studies
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-plum px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white">
            {cs.area}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">
            Professional experience · anonymised
          </span>
        </div>
        <h1 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          {cs.title}
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-muted">{cs.intro}</p>
      </Reveal>

      <Reveal className="mt-10">
        <div className="rounded-2xl border border-line bg-lavender p-8 sm:p-10" data-testid="case-study-outcome-panel">
          <SectionLabel>Achieved results</SectionLabel>
          {cs.results.length ? (
            <ul className="mt-5 space-y-4">
              {cs.results.map((v) => (
                <li key={v.text} className="font-serif text-lg leading-snug text-ink sm:text-xl">
                  {v.text}
                  {v.note && <span className="mt-1 block font-sans text-xs text-muted">{v.note}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm leading-relaxed text-muted">
              No numerical results are claimed for this work — only the described
              outcome, which reflects my professional experience.
            </p>
          )}
        </div>
      </Reveal>

      <div className="mt-12 space-y-10">
        {[
          ["Business context", cs.context],
          ["The problem", cs.problem],
          ["My role", cs.role],
        ].map(([h, body]) => (
          <Reveal key={h}>
            <section>
              <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">{h}</h2>
              <p className="mt-3 leading-relaxed text-muted">{body}</p>
            </section>
          </Reveal>
        ))}
        <Reveal>
          <section>
            <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">Investigation and approach</h2>
            <ol className="mt-4 space-y-3">
              {cs.approach.map((a, i) => (
                <li key={a} className="flex gap-4">
                  <span className="font-mono text-sm font-semibold text-accent">{String(i + 1).padStart(2, "0")}</span>
                  <span className="leading-relaxed text-muted">{a}</span>
                </li>
              ))}
            </ol>
          </section>
        </Reveal>
        <Reveal>
          <section>
            <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">The analysis, for you to explore</h2>
            <div className="mt-5"><CaseVisual cs={cs} dashboards={dashboards} process={process} /></div>
          </section>
        </Reveal>
        <Reveal>
          <section className="grid grid-cols-1 gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">Findings</h2>
              <ul className="mt-4 space-y-3">
                {cs.findings.map((x) => (
                  <li key={x} className="flex gap-2 text-sm leading-relaxed text-muted">
                    <span aria-hidden="true" className="text-accent">—</span>{x}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">Deliverables</h2>
              <ul className="mt-4 space-y-2">
                {cs.deliverables.map((d) => (
                  <li key={d} className="flex gap-2 text-sm text-ink">
                    <span aria-hidden="true" className="text-accent">—</span>{d}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>
        <Reveal>
          <section>
            <h2 className="font-serif text-xl font-medium text-ink sm:text-2xl">Tools used</h2>
            <ul className="mt-4 flex flex-wrap gap-2">
              {cs.tools.map((t) => <li key={t}><Tag>{t}</Tag></li>)}
            </ul>
          </section>
        </Reveal>
      </div>

      <Reveal className="mt-14">
        <div className="rounded-2xl border border-line bg-lavender p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Work with me</p>
          <div className="mt-4 flex flex-wrap gap-4">
            {relatedService && (
              <ButtonLink to={`/?service=${encodeURIComponent(relatedService.name)}#contact`} testid={`case-discuss-service-${cs.id}`}>
                {`Discuss ${relatedService.name.toLowerCase()}`}
              </ButtonLink>
            )}
            {relatedPackage && (
              <ButtonLink to={`/?service=${encodeURIComponent(relatedPackage.name)}#contact`} variant="secondary" testid={`case-discuss-package-${cs.id}`}>
                {relatedPackage.cta}
              </ButtonLink>
            )}
          </div>
        </div>
      </Reveal>
    </main>
  );
}
